import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import {
  signInWithEmail,
  beginMfaSignIn,
  getActiveMfaSession,
  clearActiveMfaSession,
  initRecaptchaVerifier,
  clearRecaptchaVerifier,
  sendPhoneVerificationCode,
  completeMfaSignIn,
  getMfaHintLabel,
  isPhoneMfaHint,
  isRecaptchaError,
  formatAuthError,
  getAuthErrorCode,
} from '../services/mfaAuth';

const RECAPTCHA_CONTAINER_ID = 'dd-mfa-recaptcha';

const GOLD = '#C9A84C';
const BG = '#090909';
const CARD_BG = '#111111';
const TEXT_MUTED = '#F5F0E8';
const GOLD_BORDER = 'rgba(201, 168, 76, 0.35)';
const GOLD_BORDER_FOCUS = 'rgba(201, 168, 76, 0.65)';

const inputBaseStyle = {
  width: '100%',
  borderRadius: '0.5rem',
  border: `1px solid ${GOLD_BORDER}`,
  backgroundColor: CARD_BG,
  padding: '0.75rem 1rem',
  color: '#F5F0E8',
  fontSize: '1rem',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
};

const submitButtonStyle = {
  width: '100%',
  borderRadius: '9999px',
  backgroundColor: GOLD,
  padding: '0.75rem 1rem',
  fontWeight: '700',
  color: '#000000',
  border: 'none',
  cursor: 'pointer',
  fontSize: '1rem',
  transition: 'opacity 0.2s ease, box-shadow 0.2s ease',
  boxShadow: '0 2px 12px rgba(201, 168, 76, 0.35)',
};

const handleInputFocus = (e) => {
  e.target.style.borderColor = GOLD;
  e.target.style.boxShadow = '0 0 0 2px rgba(201, 168, 76, 0.2)';
};

const handleInputBlur = (e) => {
  e.target.style.borderColor = GOLD_BORDER;
  e.target.style.boxShadow = 'none';
};

export default function AuthModal({
  isOpen,
  onClose,
  onSuccess,
  initialTab = 'login',
  loginOnly = false,
  title = null,
}) {
  const { register, resetPassword, loginWithGoogle } = useAuth();
  const [tab, setTab] = useState(loginOnly ? 'login' : initialTab);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [mfaStepActive, setMfaStepActive] = useState(false);
  const [mfaHint, setMfaHint] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [recaptchaSolved, setRecaptchaSolved] = useState(false);
  const [recaptchaLoading, setRecaptchaLoading] = useState(false);
  const [recaptchaKey, setRecaptchaKey] = useState(0);
  const recaptchaVerifierRef = useRef(null);
  const recaptchaContainerIdRef = useRef(`${RECAPTCHA_CONTAINER_ID}-0`);
  const recaptchaSetupGenerationRef = useRef(0);
  const sendInFlightRef = useRef(false);

  const getRecaptchaContainerId = (key = recaptchaKey) => `${RECAPTCHA_CONTAINER_ID}-${key}`;

  const resetMfaState = () => {
    setMfaStepActive(false);
    setMfaHint(null);
    setMfaCode('');
    setVerificationId(null);
    setPhoneCodeSent(false);
    setRecaptchaReady(false);
    setRecaptchaSolved(false);
    setRecaptchaLoading(false);
    recaptchaSetupGenerationRef.current += 1;
    clearRecaptchaVerifier(recaptchaVerifierRef.current, recaptchaContainerIdRef.current);
    recaptchaVerifierRef.current = null;
    setRecaptchaKey(0);
    recaptchaContainerIdRef.current = getRecaptchaContainerId(0);
    clearActiveMfaSession();
  };

  const activateMfaStep = (hints) => {
    const hint = hints[0];
    if (!hint) {
      throw new Error('No MFA method is configured for this account.');
    }
    setError('');
    setMfaStepActive(true);
    setMfaHint(hint);
  };

  useEffect(() => {
    if (isOpen) {
      setTab(initialTab);
      setRegisterSuccess(false);
      setError('');

      const pending = getActiveMfaSession();
      if (pending) {
        setMfaStepActive(true);
        setMfaHint(pending.hints[0]);
      } else {
        resetMfaState();
      }
    }
    return () => {
      recaptchaSetupGenerationRef.current += 1;
      clearRecaptchaVerifier(recaptchaVerifierRef.current, recaptchaContainerIdRef.current);
      recaptchaVerifierRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialTab]);

  useEffect(() => {
    if (
      !isOpen ||
      !mfaStepActive ||
      !mfaHint ||
      !isPhoneMfaHint(mfaHint) ||
      phoneCodeSent ||
      !getActiveMfaSession()
    ) {
      return undefined;
    }

    const containerId = getRecaptchaContainerId(recaptchaKey);
    recaptchaContainerIdRef.current = containerId;
    const generation = recaptchaSetupGenerationRef.current + 1;
    recaptchaSetupGenerationRef.current = generation;
    let cancelled = false;

    const setupRecaptcha = async () => {
      try {
        setRecaptchaReady(false);
        setRecaptchaSolved(false);
        setRecaptchaLoading(true);
        setError('');
        clearRecaptchaVerifier(recaptchaVerifierRef.current, containerId);
        recaptchaVerifierRef.current = null;

        await new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });
        if (cancelled || generation !== recaptchaSetupGenerationRef.current) return;

        if (!document.getElementById(containerId)) {
          throw new Error('reCAPTCHA container not found.');
        }

        const verifier = await initRecaptchaVerifier(containerId, {
          size: 'normal',
          onSolved: () => {
            if (!cancelled && generation === recaptchaSetupGenerationRef.current) {
              setRecaptchaSolved(true);
              setError('');
            }
          },
          onExpired: () => {
            if (!cancelled && generation === recaptchaSetupGenerationRef.current) {
              setRecaptchaSolved(false);
              setError('reCAPTCHA expired. Check the box again, then click Send.');
            }
          },
        });
        if (cancelled || generation !== recaptchaSetupGenerationRef.current) {
          clearRecaptchaVerifier(verifier, containerId);
          return;
        }
        recaptchaVerifierRef.current = verifier;
        setRecaptchaReady(true);
      } catch (e) {
        if (!cancelled && generation === recaptchaSetupGenerationRef.current) {
          const message = formatAuthError(e);
          setError(message);
          if (String(e?.message || '').includes('already been rendered')) {
            setRecaptchaKey((key) => key + 1);
          }
        }
      } finally {
        if (!cancelled && generation === recaptchaSetupGenerationRef.current) {
          setRecaptchaLoading(false);
        }
      }
    };

    setupRecaptcha();

    return () => {
      cancelled = true;
      clearRecaptchaVerifier(recaptchaVerifierRef.current, containerId);
      recaptchaVerifierRef.current = null;
    };
  }, [isOpen, mfaStepActive, mfaHint, phoneCodeSent, recaptchaKey]);

  if (!isOpen) return null;

  const activeMfaSession = getActiveMfaSession();
  const showMfaStep = mfaStepActive && activeMfaSession && mfaHint;

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError('');

      const result = await signInWithEmail(email, password);

      if (result.mfaRequired) {
        activateMfaStep(result.hints);
        return;
      }

      onSuccess?.();
      onClose?.();
    } catch (e) {
      try {
        const mfa = beginMfaSignIn(e);
        if (mfa) {
          activateMfaStep(mfa.hints);
          return;
        }
      } catch (mfaError) {
        setError(formatAuthError(mfaError));
        return;
      }
      setError(formatAuthError(e));
    } finally {
      setLoading(false);
    }
  };

  const resetRecaptchaWidget = () => {
    clearRecaptchaVerifier(recaptchaVerifierRef.current, recaptchaContainerIdRef.current);
    recaptchaVerifierRef.current = null;
    setRecaptchaReady(false);
    setRecaptchaSolved(false);
    setRecaptchaKey((key) => key + 1);
  };

  const handleSendPhoneCode = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    if (sendInFlightRef.current) {
      return;
    }

    const session = getActiveMfaSession();
    if (!session || !mfaHint) {
      setError('Your sign-in session expired. Please log in again.');
      resetMfaState();
      return;
    }
    if (!recaptchaVerifierRef.current) {
      setError('Verification is still loading. Please wait a moment and try again.');
      return;
    }
    if (!recaptchaSolved) {
      setError('Check “I’m not a robot” above, then click Send Verification Code.');
      return;
    }

    try {
      sendInFlightRef.current = true;
      setLoading(true);
      setError('');

      const vid = await sendPhoneVerificationCode(
        session.resolver,
        mfaHint,
        recaptchaVerifierRef.current
      );
      setVerificationId(vid);
      setPhoneCodeSent(true);
      setRecaptchaSolved(false);
      clearRecaptchaVerifier(recaptchaVerifierRef.current, recaptchaContainerIdRef.current);
      recaptchaVerifierRef.current = null;
      setRecaptchaReady(false);
    } catch (e) {
      console.error('MFA send verification code failed:', e);
      setError(formatAuthError(e));
      if (
        isRecaptchaError(e) ||
        String(e?.message || '').includes('timed out') ||
        getAuthErrorCode(e) === 'auth/missing-multi-factor-session' ||
        getAuthErrorCode(e) === 'auth/invalid-multi-factor-session'
      ) {
        if (
          getAuthErrorCode(e) === 'auth/missing-multi-factor-session' ||
          getAuthErrorCode(e) === 'auth/invalid-multi-factor-session'
        ) {
          resetMfaState();
        } else {
          resetRecaptchaWidget();
        }
      }
    } finally {
      sendInFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleVerifyMfa = async () => {
    const session = getActiveMfaSession();
    if (!session || !mfaHint) return;

    try {
      setLoading(true);
      setError('');

      await completeMfaSignIn(session.resolver, mfaHint, mfaCode.trim(), verificationId);

      resetMfaState();
      setError('');
      onSuccess?.();
      onClose?.();
    } catch (e) {
      setError(formatAuthError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    resetMfaState();
    setError('');
  };

  const handleRegister = async () => {
    try {
      setLoading(true);
      setError('');
      await register(email, password, displayName);
      setRegisterSuccess(true);
    } catch (e) {
      setError(e.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterDismiss = () => {
    setRegisterSuccess(false);
    onSuccess?.();
    onClose?.();
  };

  const isLoginTab = tab === 'login' || tab === 'reset';

  const tabColor = (active) => (active ? GOLD : TEXT_MUTED);
  const tabBorder = (active) => (active ? `2px solid ${GOLD}` : '2px solid transparent');

  return (
    <div
      className="dd-auth-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        className="dd-auth-modal"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '28rem',
          borderRadius: '1rem',
          backgroundColor: BG,
          color: '#F5F0E8',
          border: `1px solid ${GOLD_BORDER}`,
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.6), 0 0 40px rgba(201, 168, 76, 0.08)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: '0.75rem',
            right: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0.25rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: TEXT_MUTED,
            transition: 'color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = GOLD;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = TEXT_MUTED;
          }}
        >
          <X size={20} />
        </button>

        <div
          style={{
            padding: '1.5rem 1.5rem 1rem',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              marginBottom: '0.25rem',
            }}
          >
            <img
              src="/logo.webp"
              alt="Digital Divot"
              style={{
                width: '2rem',
                height: '2rem',
                objectFit: 'contain',
              }}
            />
            <span
              style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: GOLD,
                letterSpacing: '0.03em',
              }}
            >
              Digital Divot
            </span>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            padding: '0 1.5rem',
            borderBottom: `1px solid ${GOLD_BORDER}`,
          }}
        >
          {loginOnly ? (
            <div
              style={{
                flex: 1,
                padding: '0.75rem 0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: GOLD,
                borderBottom: `2px solid ${GOLD}`,
                textAlign: 'center',
              }}
            >
              {title || 'Admin Sign In'}
            </div>
          ) : (
            [
              { id: 'login', label: 'Log In' },
              { id: 'register', label: 'Create Account' },
            ].map(({ id, label }) => {
              const active = (isLoginTab && id === 'login') || tab === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  style={{
                    flex: 1,
                    padding: '0.75rem 0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: tabColor(active),
                    borderBottom: tabBorder(active),
                    background: 'none',
                    borderTop: 'none',
                    borderLeft: 'none',
                    borderRight: 'none',
                    cursor: 'pointer',
                    transition: 'color 0.2s ease, border-color 0.2s ease',
                  }}
                >
                  {label}
                </button>
              );
            })
          )}
        </div>

        <div
          style={{
            padding: '1.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          {error && (
            <div
              style={{
                borderRadius: '0.5rem',
                border: '1px solid rgba(239, 68, 68, 0.4)',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                padding: '0.75rem',
                fontSize: '0.875rem',
                color: '#fca5a5',
              }}
            >
              {error}
            </div>
          )}

          {showMfaStep ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
              }}
              style={{ display: 'flex', flexDirection: 'column', gap: '1rem', margin: 0 }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: '0.9375rem',
                  color: TEXT_MUTED,
                  lineHeight: 1.6,
                }}
              >
                Two-factor authentication is required for this account. Complete verification
                using <strong style={{ color: GOLD }}>{getMfaHintLabel(mfaHint)}</strong>.
              </p>

              {isPhoneMfaHint(mfaHint) && !phoneCodeSent ? (
                <>
                  <div
                    key={recaptchaKey}
                    id={getRecaptchaContainerId(recaptchaKey)}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      minHeight: '78px',
                      marginBottom: '0.25rem',
                    }}
                  />
                  {recaptchaLoading && (
                    <p style={{ margin: 0, fontSize: '0.8125rem', color: TEXT_MUTED, textAlign: 'center' }}>
                      Loading verification…
                    </p>
                  )}
                  {recaptchaReady && !recaptchaSolved && !recaptchaLoading && (
                    <p style={{ margin: 0, fontSize: '0.8125rem', color: TEXT_MUTED, textAlign: 'center' }}>
                      Check &ldquo;I&apos;m not a robot&rdquo; above, then click Send.
                    </p>
                  )}
                  {recaptchaSolved && (
                    <p style={{ margin: 0, fontSize: '0.8125rem', color: GOLD, textAlign: 'center' }}>
                      reCAPTCHA complete — click Send to receive your code.
                    </p>
                  )}
                  <button
                    type="button"
                    className="dd-auth-submit"
                    style={{
                      ...submitButtonStyle,
                      cursor: loading || !recaptchaSolved ? 'not-allowed' : 'pointer',
                      opacity: loading || !recaptchaSolved ? 0.6 : 1,
                    }}
                    disabled={loading || !recaptchaSolved || recaptchaLoading}
                    onClick={handleSendPhoneCode}
                  >
                    {loading ? 'Sending…' : 'Send Verification Code'}
                  </button>
                </>
              ) : (
                <>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: GOLD, textAlign: 'center' }}>
                    Enter the code sent to {getMfaHintLabel(mfaHint)}.
                  </p>
                  <input
                    className="dd-auth-input"
                    style={inputBaseStyle}
                    placeholder={isPhoneMfaHint(mfaHint) ? 'SMS code' : 'Authenticator code'}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                  <button
                    type="button"
                    className="dd-auth-submit"
                    style={{
                      ...submitButtonStyle,
                      cursor: loading || !mfaCode.trim() ? 'not-allowed' : 'pointer',
                      opacity: loading || !mfaCode.trim() ? 0.6 : 1,
                    }}
                    disabled={loading || !mfaCode.trim()}
                    onClick={handleVerifyMfa}
                  >
                    Verify &amp; Sign In
                  </button>
                </>
              )}

              <button
                type="button"
                onClick={handleBackToLogin}
                style={{
                  padding: 0,
                  background: 'none',
                  border: 'none',
                  fontSize: '0.8125rem',
                  color: TEXT_MUTED,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                  alignSelf: 'flex-start',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = GOLD;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = TEXT_MUTED;
                }}
              >
                Back to Log In
              </button>
            </form>
          ) : tab === 'login' && (
            <>
              <input
                className="dd-auth-input"
                style={inputBaseStyle}
                placeholder="Email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
              <div>
                <input
                  className="dd-auth-input"
                  style={inputBaseStyle}
                  placeholder="Password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
                <button
                  type="button"
                  onClick={() => setTab('reset')}
                  style={{
                    marginTop: '0.5rem',
                    padding: 0,
                    background: 'none',
                    border: 'none',
                    fontSize: '0.8125rem',
                    color: TEXT_MUTED,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = GOLD;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = TEXT_MUTED;
                  }}
                >
                  Forgot password?
                </button>
              </div>
              <button
                type="button"
                className="dd-auth-submit"
                style={{
                  ...submitButtonStyle,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                }}
                disabled={loading}
                onClick={handleLogin}
              >
                Log In
              </button>
              {!loginOnly && (
              <button
                type="button"
                className="dd-auth-google"
                style={{
                  width: '100%',
                  borderRadius: '9999px',
                  border: `1px solid ${GOLD_BORDER}`,
                  padding: '0.75rem 1rem',
                  fontWeight: '600',
                  color: '#F5F0E8',
                  backgroundColor: CARD_BG,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  transition: 'border-color 0.2s ease, background-color 0.2s ease',
                }}
                disabled={loading}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = GOLD_BORDER_FOCUS;
                  e.currentTarget.style.backgroundColor = '#1a1a1a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = GOLD_BORDER;
                  e.currentTarget.style.backgroundColor = CARD_BG;
                }}
                onClick={async () => {
                  try {
                    setLoading(true);
                    setError('');
                    await loginWithGoogle();
                    onSuccess?.();
                    onClose?.();
                  } catch (e) {
                    setError(e.message || 'Authentication failed');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Continue with Google
              </button>
              )}
            </>
          )}

          {tab === 'register' && registerSuccess && (
            <>
              <p
                style={{
                  margin: 0,
                  fontSize: '0.9375rem',
                  color: TEXT_MUTED,
                  lineHeight: 1.6,
                  textAlign: 'center',
                }}
              >
                Check your inbox to verify your email before joining The League.
              </p>
              <button
                type="button"
                className="dd-auth-submit"
                style={submitButtonStyle}
                onClick={handleRegisterDismiss}
              >
                Got it
              </button>
            </>
          )}

          {tab === 'register' && !registerSuccess && (
            <>
              <input
                className="dd-auth-input"
                style={inputBaseStyle}
                placeholder="Full name (optional)"
                autoComplete="name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
              <input
                className="dd-auth-input"
                style={inputBaseStyle}
                placeholder="Email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
              <input
                className="dd-auth-input"
                style={inputBaseStyle}
                placeholder="Password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
              <button
                type="button"
                className="dd-auth-submit"
                style={{
                  ...submitButtonStyle,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                }}
                disabled={loading}
                onClick={handleRegister}
              >
                Create Account
              </button>
            </>
          )}

          {tab === 'reset' && (
            <>
              <p
                style={{
                  margin: 0,
                  fontSize: '0.875rem',
                  color: TEXT_MUTED,
                  lineHeight: '1.5',
                }}
              >
                Enter your email and we&apos;ll send you a reset link.
              </p>
              <input
                className="dd-auth-input"
                style={inputBaseStyle}
                placeholder="Email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
              <button
                type="button"
                className="dd-auth-submit"
                style={{
                  ...submitButtonStyle,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                }}
                disabled={loading}
                onClick={async () => {
                  try {
                    setLoading(true);
                    setError('');
                    await resetPassword(email);
                    onSuccess?.();
                    onClose?.();
                  } catch (e) {
                    setError(e.message || 'Authentication failed');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Send Reset Link
              </button>
              <button
                type="button"
                onClick={() => setTab('login')}
                style={{
                  padding: 0,
                  background: 'none',
                  border: 'none',
                  fontSize: '0.8125rem',
                  color: TEXT_MUTED,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                  alignSelf: 'flex-start',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = GOLD;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = TEXT_MUTED;
                }}
              >
                Back to Log In
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
